import {
  GRAPHEME_SVG_GLYPH_DASHOFFSET_FACTOR,
  GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH,
} from "#grapheme-svg-glyph-animation";
import type {
  GraphemeStrokeRevealGeometry,
  GraphemeStrokeRevealJoin,
  GraphemeStrokeRevealPoint,
  GraphemeStrokeRevealSegment,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.svg.entities";

// SVG's default. The existing player explicitly uses miter joins and leaves this default alone.
const MITER_LIMIT = 4;

function makeJoin(
  previous: GraphemeStrokeRevealSegment,
  next: GraphemeStrokeRevealSegment,
  radius: number,
): GraphemeStrokeRevealJoin | undefined {
  const [ux, uy] = previous.direction;
  const [vx, vy] = next.direction;
  const cross = ux * vy - uy * vx;
  // A straight continuation needs no polygon. A perfect reversal bevels to the coincident
  // butt ends: adding a round cap here would incorrectly retain the old advancing cap.
  if (cross === 0) {
    return undefined;
  }
  const outside = cross > 0 ? -1 : 1;
  const [x, y] = next.start;
  const first: GraphemeStrokeRevealPoint = [x - outside * uy * radius, y + outside * ux * radius];
  const last: GraphemeStrokeRevealPoint = [x - outside * vy * radius, y + outside * vx * radius];
  const denominator = 1 + ux * vx + uy * vy;
  const miterRatioSquared = denominator > 0 ? 2 / denominator : Number.POSITIVE_INFINITY;
  const points: GraphemeStrokeRevealPoint[] = [next.start, first];
  if (miterRatioSquared <= MITER_LIMIT * MITER_LIMIT) {
    points.push([
      x - (outside * (uy + vy) * radius) / denominator,
      y + (outside * (ux + vx) * radius) / denominator,
    ]);
  }
  points.push(last);
  return { offset: next.offset, points: cross > 0 ? points : points.reverse() };
}

/**
 * Immutable geometry for the exact prefix of the player's 200-unit median stroke. Each prefix
 * consists of butt-ended segment rectangles, the outer miter/bevel polygons at crossed vertices,
 * and ONLY its two outward-facing semicircular caps. A full disk at the moving tip can protrude
 * beyond an acute bevel when the newly drawn segment is shorter than the radius. Unioning
 * capsules for every segment also cannot model coverage disappearing at a sharp corner.
 */
export function buildGraphemeStrokeRevealGeometry(
  median: readonly GraphemeStrokeRevealPoint[],
): GraphemeStrokeRevealGeometry {
  for (const [x, y] of median) {
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      throw new TypeError("Stroke median coordinates must be finite");
    }
  }
  const radius = GRAPHEME_SVG_GLYPH_MEDIAN_STROKE_WIDTH / 2;
  const first = median[0] ?? [0, 0];
  const second = median[1];
  let start: GraphemeStrokeRevealPoint = first;
  if (second !== undefined) {
    const dx = second[0] - first[0];
    const dy = second[1] - first[1];
    const length = Math.hypot(dx, dy);
    // Match Hanzi Writer's extendStart exactly, including its duplicate-first-point behavior.
    if (length > 0) {
      start = [first[0] - (dx * radius) / length, first[1] - (dy * radius) / length];
    }
  }
  const segments: GraphemeStrokeRevealSegment[] = [];
  const joins: GraphemeStrokeRevealJoin[] = [];
  let previousPoint = start;
  let length = 0;
  for (const point of median.slice(1)) {
    const dx = point[0] - previousPoint[0];
    const dy = point[1] - previousPoint[1];
    const segmentLength = Math.hypot(dx, dy);
    if (segmentLength > 0) {
      const segment: GraphemeStrokeRevealSegment = {
        direction: [dx / segmentLength, dy / segmentLength],
        length: segmentLength,
        offset: length,
        start: previousPoint,
      };
      const previousSegment = segments.at(-1);
      if (previousSegment !== undefined) {
        const join = makeJoin(previousSegment, segment, radius);
        if (join !== undefined) {
          joins.push(join);
        }
      }
      segments.push(segment);
      length += segmentLength;
    }
    previousPoint = point;
  }
  return { hasDrawingCommand: median.length > 1, joins, length, radius, segments, start };
}

export function getGraphemeStrokeRevealLength(
  geometry: GraphemeStrokeRevealGeometry,
  localProgress: number,
): number {
  const progress = Math.max(0, Math.min(1, localProgress));
  // The player clamps dasharray length to one for degenerate medians.
  const dashLength = Math.max(1, geometry.length);
  return Math.min(
    geometry.length,
    dashLength - dashLength * GRAPHEME_SVG_GLYPH_DASHOFFSET_FACTOR * (1 - progress),
  );
}

function pointInJoin(point: GraphemeStrokeRevealPoint, join: GraphemeStrokeRevealJoin): boolean {
  return join.points.every((start, index) => {
    const end = join.points[(index + 1) % join.points.length];
    return (
      end !== undefined &&
      (end[0] - start[0]) * (point[1] - start[1]) - (end[1] - start[1]) * (point[0] - start[0]) >= 0
    );
  });
}

/** CPU coverage oracle for deterministic geometry tests; the renderer evaluates it on the GPU. */
export function isPointInGraphemeStrokeReveal(
  geometry: GraphemeStrokeRevealGeometry,
  point: GraphemeStrokeRevealPoint,
  localProgress: number,
): boolean {
  if (localProgress <= 0 || !geometry.hasDrawingCommand) {
    return false;
  }
  const length = getGraphemeStrokeRevealLength(geometry, localProgress);
  const radiusSquared = geometry.radius * geometry.radius;
  const inCap = (center: GraphemeStrokeRevealPoint, outward: GraphemeStrokeRevealPoint) => {
    const dx = point[0] - center[0];
    const dy = point[1] - center[1];
    return dx * dx + dy * dy <= radiusSquared && dx * outward[0] + dy * outward[1] >= 0;
  };
  const firstDirection: GraphemeStrokeRevealPoint = geometry.segments[0]?.direction ?? [0, 0];
  if (inCap(geometry.start, [-firstDirection[0], -firstDirection[1]])) {
    return true;
  }
  let tip = geometry.start;
  let tipDirection = firstDirection;
  for (const segment of geometry.segments) {
    if (length <= segment.offset) {
      break;
    }
    const extent = Math.min(segment.length, length - segment.offset);
    const dx = point[0] - segment.start[0];
    const dy = point[1] - segment.start[1];
    const along = dx * segment.direction[0] + dy * segment.direction[1];
    const across = dx * -segment.direction[1] + dy * segment.direction[0];
    if (along >= 0 && along <= extent && Math.abs(across) <= geometry.radius) {
      return true;
    }
    tip = [
      segment.start[0] + segment.direction[0] * extent,
      segment.start[1] + segment.direction[1] * extent,
    ];
    tipDirection = segment.direction;
  }
  return (
    inCap(tip, tipDirection) ||
    geometry.joins.some((join) => length > join.offset && pointInJoin(point, join))
  );
}
